import { lazy } from 'react';

const RETRY_DELAYS_MS = [500, 1500, 3000, 5000];
const CHUNK_CHECK_TIMEOUT_MS = 5000;
const RELOAD_STORAGE_KEY = 'chunkReloadAt';
const RELOAD_COOLDOWN_MS = 30000;

const wait = (ms) =>
    new Promise((resolve) => {
        setTimeout(resolve, ms);
    });

const waitForOnline = () => {
    if (typeof navigator === 'undefined' || navigator.onLine !== false) {
        return Promise.resolve();
    }
    return new Promise((resolve) => {
        window.addEventListener('online', resolve, { once: true });
    });
};

const isChunkGone = async (err) => {
    if (!err?.request || typeof fetch !== 'function') return false;
    try {
        const signal =
            typeof AbortSignal !== 'undefined' && AbortSignal.timeout
                ? AbortSignal.timeout(CHUNK_CHECK_TIMEOUT_MS)
                : undefined;
        const res = await fetch(err.request, {
            method: 'HEAD',
            cache: 'no-store',
            signal,
        });
        return res.status === 404;
    } catch {
        return false;
    }
};

const reloadOnce = () => {
    try {
        const last = Number(sessionStorage.getItem(RELOAD_STORAGE_KEY)) || 0;
        if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
        sessionStorage.setItem(RELOAD_STORAGE_KEY, String(Date.now()));
    } catch {
        if (window.__chunkReloadAttempted) return false;
        window.__chunkReloadAttempted = true;
    }
    window.location.reload();
    return true;
};

export const importWithRetry = async (
    importFn,
    { reloadOnFailure = false } = {},
) => {
    for (let attempt = 0; ; attempt += 1) {
        try {
            return await importFn();
        } catch (err) {
            if (typeof window === 'undefined') throw err;
            const gone = await isChunkGone(err);
            if (gone || attempt >= RETRY_DELAYS_MS.length) {
                if ((gone || reloadOnFailure) && reloadOnce()) {
                    return new Promise(() => {});
                }
                throw err;
            }
            await waitForOnline();
            await wait(RETRY_DELAYS_MS[attempt]);
        }
    }
};

export const lazyWithRetry = (importFn) =>
    lazy(() => importWithRetry(importFn, { reloadOnFailure: true }));
